import { EEntityType, entityTypeToLink } from '@sharedInterfaces/globalEnums';
import { getLanguageTexts } from '@sharedInterfaces/Language/languageHelper';
import CardBox from '@src/App/Components/Cards/CardBox/CardBox';
import RoleItem from '@src/App/Components/Items/EntityItems/RoleItem/RoleItem';
import List, { generateTopXText } from '@src/App/Components/Lists/List/List';
import { EIconCategory, getAppIcon } from '@src/helper/iconRegistry';
import type { AppState } from '@store/store';
import React from 'react';
import { useSelector } from 'react-redux';

interface RolesCardProps {
    title?: string;
    roles: {
        title: string;
        id: number;
        lockedBy?: string;
    }[];
    inOverlay?: boolean;
    limit?: number;
    rightButtons?: React.ReactNode;
}

/**
 * RolesCard component.
 *
 * @param {RolesCardProps} title - The title of the box.
 * @param {RolesCardProps} roles - The roles to display.
 * @param {RolesCardProps} inOverlay - Flag indicating if the box is in overlay mode.
 * @returns {JSX.Element} The RolesCard as a JSX element.
 */
function RolesCard({ limit, title, roles, inOverlay, rightButtons }: RolesCardProps) {
    if (!roles) roles = [];
    roles = [...roles].sort((a, b) => {
        return a.title.localeCompare(b.title);
    });
    const lang = useSelector((state: AppState) => state.employee.language);
    const langStrings = getLanguageTexts(lang).roles;
    const newTitle = generateTopXText(title ? title : langStrings.roles, limit);
    const limitedElements = limit ? roles.slice(0, limit > roles.length ? roles.length : limit) : roles;
    const DOM = limitedElements.map((role) => {
        return (
            <RoleItem
                key={role.id}
                id={role.id}
                title={role.title}
                lockedBy={role.lockedBy}
                disableOverlay={inOverlay}
                onClick={entityTypeToLink(role.id, EEntityType.ROLE)}
            />
        );
    });
    return (
        <CardBox title={newTitle} showIcon={true} icon={getAppIcon(EIconCategory.ROLES)} rightButtons={rightButtons}>
            <List>{DOM}</List>
        </CardBox>
    );
}

export default RolesCard;
